/**
 * Catálogo de tipologías de gasto en el cliente (mismo patrón que service-lines).
 * useExpenseCategories() carga GET /expense-categories una vez por sesión y expone:
 *  - categories: solo activas, ordenadas (para el selector del formulario de gasto).
 *  - allCategories: todas (para resolver nombres de tipologías retiradas en histórico).
 *  - nameOf(code): nombre humano con fallback a la etiqueta legada o al propio código.
 * Tras crear/editar en Ajustes, llama a invalidateExpenseCategoriesCache().
 */

import { useEffect, useState } from 'react'
import { api } from './api'
import {
  ExpenseCategoryLabel,
  type ExpenseCategoryDefDto,
  type ExpenseCategoryListResponse,
} from '@jr2os/shared'

let cache: ExpenseCategoryDefDto[] | null = null
let inflight: Promise<ExpenseCategoryDefDto[]> | null = null

async function fetchAll(): Promise<ExpenseCategoryDefDto[]> {
  if (cache) return cache
  if (!inflight) {
    inflight = api
      .get<ExpenseCategoryListResponse>('/expense-categories')
      .then((r) => {
        cache = r.items
        return r.items
      })
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

export function invalidateExpenseCategoriesCache() {
  cache = null
}

export function useExpenseCategories() {
  const [allCategories, setAllCategories] = useState<ExpenseCategoryDefDto[]>(cache ?? [])
  const [loading, setLoading] = useState(cache === null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    void fetchAll()
      .then((items) => {
        if (!cancelled) {
          setAllCategories(items)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true)
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const categories = allCategories.filter((c) => c.active)
  const defaultCode =
    categories.find((c) => c.isDefault)?.code ?? categories[0]?.code ?? 'OTROS'
  const nameOf = (code: string | null | undefined): string => {
    if (!code) return ''
    return allCategories.find((c) => c.code === code)?.name ?? ExpenseCategoryLabel[code] ?? code
  }

  return { categories, allCategories, defaultCode, nameOf, loading, failed }
}
